import React from 'react';
import { Info } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { WHITE_INK_OPTIONS } from '../lib/whiteInkEligibility';

/**
 * WhiteInkOptionsPicker — paired card selector for transparent / clear /
 * specialty products (Clear BOPP, Holographic, Foil, DTF, etc.).
 *
 * Mirrors the StickerYou/Make-Stickers UX:
 *   [Keep white in Design]  [Make white areas clear]
 * Each card shows a smiley face on a transparency-grid background to
 * visually communicate the difference. Self-contained SVG — no external
 * image assets required.
 *
 * Props:
 *   value:    'keep' | 'clear'
 *   onChange: (next) => void
 *   compact?: boolean — tighter spacing for the Page-2 size card
 */
const SmileySwatch = ({ clear }) => (
  // 60 × 60 thumbnail. The `clear` variant punches the smiley out of the
  // checkerboard (showing the transparency grid through it); the
  // non-clear variant overlays a solid white smiley on the grid.
  <svg
    width="44"
    height="44"
    viewBox="0 0 60 60"
    aria-hidden="true"
    className="shrink-0"
  >
    <defs>
      <pattern id="wio-checker" x="0" y="0" width="10" height="10" patternUnits="userSpaceOnUse">
        <rect width="10" height="10" fill="#E5E7EB" />
        <rect x="0" y="0" width="5" height="5" fill="#9CA3AF" />
        <rect x="5" y="5" width="5" height="5" fill="#9CA3AF" />
      </pattern>
      <mask id={`wio-mask-${clear ? 'clear' : 'keep'}`}>
        <rect width="60" height="60" fill="white" />
        {clear && (
          <g fill="black">
            {/* Punch out the smiley face so the checker shows through */}
            <circle cx="30" cy="30" r="20" fill="white" />
            <circle cx="30" cy="30" r="20" fill="black" />
            {/* Outline ring */}
            <circle cx="30" cy="30" r="20" fill="none" stroke="white" strokeWidth="3" />
          </g>
        )}
      </mask>
    </defs>

    {/* Background transparency grid — always visible */}
    <rect width="60" height="60" fill="url(#wio-checker)" rx="4" />

    {/* Smiley face */}
    <g
      stroke={clear ? 'transparent' : '#111827'}
      fill={clear ? 'url(#wio-checker)' : 'white'}
      strokeWidth="2.5"
    >
      <circle cx="30" cy="30" r="18" stroke="#111827" />
    </g>
    <g fill="#111827">
      <circle cx="23" cy="25" r="2.2" />
      <circle cx="37" cy="25" r="2.2" />
    </g>
    <path
      d="M22 33 Q30 41 38 33"
      stroke="#111827"
      strokeWidth="2.5"
      fill="none"
      strokeLinecap="round"
    />
  </svg>
);

const Card = ({ option, label, currentValue, onChange, testId, theme = 'light' }) => {
  const active = currentValue === option;
  // iter114q — `theme="dark"` styles the cards for the dark-emerald
  // Page2ArtworkSection background (active state amber-ringed like the
  // Cut Shape buttons, not blue). Default `light` keeps the original
  // sky-blue treatment for any legacy call-sites still mounting on a
  // white card.
  const dark = theme === 'dark';
  // iter114s.9 — selected card now pulses with the global amber ring
  // so the customer can see at-a-glance which option is currently
  // applied. Same effect used across cut shape, alt-size compare, etc.
  const activeClasses = dark
    ? 'border-amber-300 bg-amber-300/20 animate-pulse-ring'
    : 'border-[#0EA5E9] bg-sky-50 animate-pulse-ring';
  const inactiveClasses = dark
    ? 'border-white/15 bg-white/5 hover:border-amber-300/60'
    : 'border-gray-200 bg-white hover:border-[#6FBF73]/50';
  const labelClasses = dark
    ? `text-xs font-bold leading-tight ${active ? 'text-amber-200' : 'text-white'}`
    : 'text-sm sm:text-base font-semibold text-gray-800 leading-tight';
  return (
    <button
      type="button"
      onClick={() => onChange(option)}
      data-testid={testId}
      aria-pressed={active}
      className={`flex items-center gap-3 px-4 py-3 rounded-lg border-2 cursor-pointer flex-1 transition-colors text-left ${
        active ? activeClasses : inactiveClasses
      }`}
    >
      <SmileySwatch clear={option === WHITE_INK_OPTIONS.CLEAR} />
      <span className={labelClasses}>
        {label}
      </span>
    </button>
  );
};

export const WhiteInkOptionsPicker = ({ value, onChange, compact = false, theme = 'light' }) => {
  // Defensive default — first render before the parent's useEffect commits.
  const v = value || WHITE_INK_OPTIONS.KEEP;
  const dark = theme === 'dark';
  const titleClasses = dark
    ? 'text-[10px] font-bold text-amber-200/90 uppercase tracking-[0.15em]'
    : (compact
      ? 'text-sm font-bold uppercase tracking-wide text-gray-700'
      : 'text-lg font-semibold');
  const helperClasses = dark
    ? 'text-[10px] text-emerald-100/70 mt-2 leading-snug'
    : `text-xs text-gray-500 mt-2 ${compact ? '' : 'leading-snug'}`;
  const infoBtnClasses = dark
    ? 'text-amber-200/80 hover:text-amber-200 transition-colors rounded-full p-0.5 focus:outline-none focus:ring-2 focus:ring-amber-300/40'
    : 'text-gray-400 hover:text-[#4A7C59] transition-colors rounded-full p-0.5 focus:outline-none focus:ring-2 focus:ring-[#4A7C59]/40';
  return (
    <div data-testid="white-ink-options-picker">
      <div className={`flex items-center gap-1.5 ${dark ? 'mb-1.5' : 'mb-3'}`}>
        <div className={titleClasses}>
          White Ink Options
        </div>
        {/* Info tooltip — gives unfamiliar customers an a-ha moment by
            showing the same checker-grid swatches side-by-side with a
            crisp 3-line explainer. Specialty SKUs (Clear BOPP /
            Holographic / Foil / DTF) are the high-margin ones, so a
            confident understanding here lifts conversion. */}
        <Popover>
          <PopoverTrigger asChild>
            <button
              type="button"
              data-testid="white-ink-info-trigger"
              aria-label="What is white ink?"
              className={infoBtnClasses}
            >
              <Info className="w-4 h-4" />
            </button>
          </PopoverTrigger>
          <PopoverContent
            side="top"
            align="start"
            className="w-80 p-4 text-sm leading-snug text-gray-700"
            data-testid="white-ink-info-popover"
          >
            <p className="font-semibold text-gray-900 mb-2">What is white ink?</p>
            <p className="text-xs text-gray-600 mb-3">
              On clear, holographic, or foil substrates, white pixels in your
              design are <strong>printed as opaque white ink</strong>. Choose
              whether to keep them solid or let them stay transparent so the
              substrate (or surface beneath the sticker) shows through.
            </p>
            <div className="grid grid-cols-2 gap-2">
              <div className="flex flex-col items-center gap-1.5 p-2 rounded-lg border border-gray-200 bg-gray-50">
                <SmileySwatch clear={false} />
                <p className="text-[11px] text-center text-gray-700">
                  <strong>Keep white</strong>
                  <br />
                  <span className="text-gray-500">Solid white smiley</span>
                </p>
              </div>
              <div className="flex flex-col items-center gap-1.5 p-2 rounded-lg border border-gray-200 bg-gray-50">
                <SmileySwatch clear />
                <p className="text-[11px] text-center text-gray-700">
                  <strong>Make clear</strong>
                  <br />
                  <span className="text-gray-500">Substrate shows through</span>
                </p>
              </div>
            </div>
          </PopoverContent>
        </Popover>
      </div>
      <div className={compact ? 'grid grid-cols-2 gap-2' : 'grid grid-cols-2 gap-3'}>
        <Card
          option={WHITE_INK_OPTIONS.KEEP}
          label="Keep white in Design"
          currentValue={v}
          onChange={onChange}
          testId="white-ink-keep"
          theme={theme}
        />
        <Card
          option={WHITE_INK_OPTIONS.CLEAR}
          label="Make white areas clear"
          currentValue={v}
          onChange={onChange}
          testId="white-ink-clear"
          theme={theme}
        />
      </div>
      <p className={helperClasses}>
        Choose <strong>Keep white</strong> to print every white pixel as
        opaque white ink. Choose <strong>Make white clear</strong> to leave
        those areas transparent so the substrate shows through.
      </p>
    </div>
  );
};

export default WhiteInkOptionsPicker;
